import React from 'react';
import PropTypes from 'prop-types';
import {OrderedMap, List} from 'immutable';
import classnames from 'classnames';
import {Link} from 'react-router';
import {Card, Text, Icon, MenuListItem, MobileDropdown} from '@albert-io/atomic';
import ImmutablePropTypes from 'react-immutable-proptypes';

import getOverriddenDomainString from '../getOverriddenDomainString';
import './sidebar-menu.scss';

/**
 * @todo: Figure out how to make the sidebar menus open and close with animations
 */

export default class SidebarMenu extends React.Component {
  static propTypes = {
    menuItems: PropTypes.instanceOf(OrderedMap),
    makeItemHref: PropTypes.func,
    onItemClick: PropTypes.func,
    activePath: PropTypes.array,
    /**
     * Should be an ordered map of [Display text]: Url text
     * EX: 'All Courses': all-courses
     */
    additionalSidebarMenuItems: ImmutablePropTypes.orderedMap,
    group: PropTypes.string,
    subgroup: PropTypes.string
  };

  static defaultProps = {
    onItemClick: () => {},
    additionalSidebarMenuItems: OrderedMap()
  };

  /**
   * Since 'activePath' was created to traverse 'subjectMenuData' and not handle
   * outside categories such as 'Discover' and 'All Subjects' we need to do extra manual checks to
   * apply the selected state to any domains that are not in the menuData.
   *
   * 'All Subjects' exists in all instances of the sidebar and requires a special check due to being hardcoded
   *
   * @param  {string}  category
   * @param  {string}  item
   * @returns {boolean}
   */
  isItemActive(category, item) {
    const [activeCategory, activeItem] = this.props.activePath;
    if (
      (!activeCategory && category === 'All Subjects') ||
      activeCategory === category.toLowerCase()
    ) {
      return true;
    }
    const isActiveCategory = activeCategory === category;
    const isActiveItem = activeItem ? activeItem === item : item === 'All';
    return isActiveCategory && isActiveItem;
  }

  renderItems(category, items) {
    const {makeItemHref} = this.props;
    const WrapperComponent = makeItemHref ? Link : 'li';
    return (
      <ul className='cl-sidebar-menu__items-list'>
        {items.map((item, i) => (
          <MenuListItem
            as={WrapperComponent}
            selected={this.isItemActive(category, item)}
            text={item}
            to={makeItemHref ? makeItemHref(category, item) : null}
            onMenuItemClick={() => this.handleItemClick(category, item)}
            key={i}
          />
        ))}
      </ul>
    );
  }

  renderCategories = ([category, items], i) => {
    const {makeItemHref, additionalSidebarMenuItems} = this.props;

    const WrapperComponent = makeItemHref ? Link : 'div';

    const lastAdditionalItem =
      additionalSidebarMenuItems && !additionalSidebarMenuItems.isEmpty()
        ? additionalSidebarMenuItems.keySeq().last()
        : 'All Subjects';

    const shouldApplyMargin =
      (items.size !== 0 && !additionalSidebarMenuItems.has(category)) ||
      category === lastAdditionalItem;

    const isCategoryItem =
      (!additionalSidebarMenuItems || !additionalSidebarMenuItems.keySeq().includes(category)) &&
      category !== 'All Subjects';

    return (
      <li
        className={classnames('cl-sidebar-menu__category', {
          'u-mar-b_7': shouldApplyMargin
        })}
        key={i}
      >
        <MenuListItem
          isCategoryItem={isCategoryItem}
          as={WrapperComponent}
          selected={this.isItemActive(category, 'All')}
          text={getOverriddenDomainString(category) ?? category}
          onMenuItemClick={() => this.handleItemClick(category, 'All')}
          to={makeItemHref ? makeItemHref(category, 'All') : null}
        />
        {items.size ? this.renderItems(category, items) : null}
      </li>
    );
  };

  getMenuItems() {
    return OrderedMap({
      'All Subjects': List()
    })
      .merge(this.props.additionalSidebarMenuItems)
      .merge(this.props.menuItems);
  }

  handleItemClick = (category, item) => {
    this.props.onItemClick(category, item);
  };

  render() {
    const triggerElement = (
      <Card paddingLevel={2} className='cl-sidebar-menu__dropdown'>
        <div className='cl-sidebar-menu__dropdown-text'>
          <Text size='s' emphasis='lowest'>
            {this.props.group}
          </Text>
          <Text>{this.props.subgroup}</Text>
        </div>
        <Icon icon='caret-down' />
      </Card>
    );
    return (
      <MobileDropdown fillWrapper className='cl-sidebar-menu' trigger={triggerElement}>
        <ul className='cl-sidebar-menu__category-list'>
          {this.getMenuItems().entrySeq().map(this.renderCategories)}
        </ul>
      </MobileDropdown>
    );
  }
}
